<template>
  <div class="grid-container">
    <section-header size="xl" variants="dividing">
      <heading :level="1" size="xl" v-html="bp('textEditProfile')"></heading>
    </section-header>

    <form id="edit-store-profile">
      <section-header size="md">
        <heading :level="2" size="md" v-html="bp('textStoreInfo')"></heading>
      </section-header>
      <div class="grid-x grid-margin-x">
        <div class="cell large-4">
          <form-group
            id="text-store-name"
            v-model="storeData.name"
            v-validate="'required'"
            type="text"
            :data-vv-as="bp('labelStoreName')"
            :error="errors.first('text-store-name')"
            name="text-store-name"
            :label="bp('labelStoreName')"
            qaid="text-store-name"
            :show-error-messages="true"
          ></form-group>

          <form-group
            id="text-email-address"
            v-model="storeData.emailAddress"
            v-validate="{required: true, email: true}"
            type="email"
            :data-vv-as="bp('labelEmail')"
            :error="errors.first('text-email-address')"
            name="text-email-address"
            :label="bp('labelEmail')"
            qaid="text-email-address"
            :show-error-messages="true"
          ></form-group>
        </div>
        <div class="cell large-4">
          <form-group
            id="text-phone-number"
            v-model="storeData.phoneNumber"
            v-validate="{required: true}"
            type="text"
            allowed-characters-regex="[\d-+. ()]"
            :data-vv-as="bp('labelPhone')"
            :error="errors.first('text-phone-number')"
            name="text-phone-number"
            :label="bp('labelPhone')"
            qaid="text-phone-number"
            :show-error-messages="true"
          ></form-group>

          <form-group
            id="text-website-url"
            v-model="storeData.url"
            type="url"
            name="text-website-url"
            :label="bp('labelWebURL')"
            qaid="text-website-url"
          ></form-group>
        </div>
        <div class="cell large-4">
          <p class="text-sm font-bold" v-html="bp('labelStoreImage')"></p>
          <div class="mt-25">
            <img class="block w-full" :src="storeData.storeImageUrl" />
          </div>
          <p class="mt-25 text-sm" v-html="unescapeString(bp('textMissingImage'))"></p>
        </div>
      </div>

      <hr class="rule my-5" />

      <div class="grid-x">
        <div class="cell large-8">
          <section-header size="md">
            <div>
              <heading :level="2" size="md" v-html="bp('textStoreAddress')"></heading>
              <subheading v-html="bp('textMapMarker')"></subheading>
            </div>
          </section-header>
          <form-group
            id="text-address-line-1"
            v-model="storeData.line1"
            v-validate="'required'"
            type="text"
            :data-vv-as="bp('labelAddressLine1')"
            :error="errors.first('text-address-line-1')"
            name="text-address-line-1"
            :label="bp('labelAddressLine1')"
            qaid="text-address-line-1"
            :show-error-messages="true"
            @blur="updateAddress"
          ></form-group>

          <form-group
            id="text-address-line-2"
            v-model="storeData.line2"
            type="text"
            name="text-address-line-2"
            :label="bp('labelAddressLine2')"
            qaid="text-address-line-2"
          ></form-group>

          <div class="grid-x grid-margin-x">
            <div class="cell large-6">
              <form-group
                id="text-address-city"
                v-model="storeData.town"
                v-validate="'required'"
                type="text"
                :data-vv-as="bp('labelTownCity')"
                :error="errors.first('text-address-city')"
                name="text-address-city"
                :label="bp('labelTownCity')"
                qaid="text-address-city"
                :show-error-messages="true"
                @blur="updateAddress"
              ></form-group>

              <form-group
                id="text-address-postal-code"
                v-model="storeData.postalCode"
                v-mask="postCodeFormatOptions"
                v-validate="{required: true, length: isPostCodeLength}"
                type="text"
                :data-vv-as="bp('labelPostalCode')"
                :error="errors.first('text-address-postal-code')"
                name="text-address-postal-code"
                :label="bp('labelPostalCode')"
                qaid="text-address-postal-code"
                :show-error-messages="true"
                @blur="updateAddress"
                @input="postCodeToUppercase"
              ></form-group>
            </div>
            <div class="cell large-6">
              <fieldset class="form-fieldset">
                <label for="state" class="form-label" v-html="bp('labelSelectState')"></label>
                <input
                  v-if="storeData.countryName !== 'United States'"
                  id="state"
                  v-model="storeData.regionIso"
                  type="text"
                  name="state"
                  class="form-control"
                  @blur="updateAddress"
                />

                <div
                  v-if="storeData.countryName === 'United States'"
                  class="select"
                  :class="{'has-error': !!errors.first('text-address-state')}"
                >
                  <select
                    id="state"
                    v-model="storeData.regionIso"
                    v-validate="'required'"
                    required="required"
                    class="form-control"
                    :data-vv-as="bp('labelSelectState')"
                    name="text-address-state"
                    @change="updateAddress"
                  >
                    <option
                      v-for="(state, stateIndex) in storeData.options.countryRegionMap.US"
                      :key="stateIndex"
                      :value="values(state)[0].isocode"
                      :selected="storeData.regionIso === values(state)[0].isocode"
                      >{{ values(state)[0].name }}
                    </option>
                  </select>
                </div>

                <div v-show="errors.first('text-address-state')" class="form-feedback is-invalid">
                  {{ errors.first('text-address-state') }}
                </div>
              </fieldset>
              <fieldset class="form-fieldset">
                <label for="country" class="form-label" v-html="bp('labelCountry')"></label>
                <div class="select">
                  <select
                    id="country"
                    v-model="storeData.countryIso"
                    required="required"
                    class="form-control"
                    @change="updateCountryName"
                  >
                    <option
                      v-for="(country, index) in storeData.options.countries"
                      :key="index"
                      :value="country.isoCode"
                      >{{ country.name }}
                    </option>
                  </select>
                </div>
              </fieldset>
            </div>
          </div>

          <div class="grid-x grid-margin-x">
            <div class="cell large-6">
              <form-group
                id="text-address-delivery-radius"
                v-model="storeData.deliveryRadius"
                v-validate="'required|decimal:0|max_value:' + maxRadius"
                type="text"
                :data-vv-as="bp('labelDeliveryRadius')"
                :error="errors.first('text-address-delivery-radius')"
                name="text-address-delivery-radius"
                :label="bp('labelDeliveryRadius')"
                qaid="text-address-delivery-radius"
                :show-error-messages="true"
              ></form-group>
            </div>
            <div class="cell large-6">
              <fieldset class="form-fieldset" :class="{'has-error': !!distanceUnitError}">
                <legend class="form-fieldset__legend font-bold" qaid="text-address-delivery-radius-unit-label">
                  {{ bp('labelDeliveryRadiusUnit') }}
                </legend>
                <label v-for="unit in distanceUnits" :key="`unit-${unit.code}`" class="radio">
                  <input
                    :id="`delivery-radius-unit-${unit.code}`"
                    v-model="storeData.deliveryRadiusUnit"
                    v-validate="'required'"
                    type="radio"
                    :value="unit.code"
                    name="delivery-radius-unit"
                    qaid="`delivery-radius-unit-${unit.code}`"
                    :data-vv-as="bp('labelDeliveryRadiusUnit')"
                  />
                  <span class="control-indicator"></span>
                  {{ unit.name }}
                </label>
                <span v-if="distanceUnitError" class="form-feedback is-invalid" v-html="distanceUnitError"></span>
              </fieldset>
            </div>
          </div>
          <h5 class="font-medium" v-html="bp('labelStoreMap')"></h5>
          <p>
            <em v-html="bp('textMovePin')"></em>
          </p>
          <div>
            <store-location-map
              ref="storeMap"
              :initial-coordinates="{
                lat: location.latitude,
                lng: location.longitude,
              }"
              :address="formattedAddress"
              map-classes="b2b-map"
              :zoom="16"
              :show-debug="false"
              class="my-2"
              @map-changed="updateLatLng"
              @pin-drop="updateLatLng"
            />
          </div>
        </div>
      </div>

      <hr class="rule my-5" />

      <div class="grid-x">
        <div class="cell large-7">
          <div>
            <section-header size="md">
              <div>
                <heading :level="2" size="md" v-html="bp('textStoreHours')"></heading>
                <subheading v-html="bp('textSelectHours')"></subheading>
                <fieldset class="form-fieldset max-w-xs mt-2 mb-0">
                  <label for="time-zone" class="form-label" v-html="bp('labelSelectTimeZone')"></label>
                  <div class="select">
                    <select
                      id="time-zone"
                      v-model="storeData.timeZone"
                      :value="storeData.timeZone"
                      required="required"
                      class="form-control"
                    >
                      <option v-for="(zone, zoneKey) in timeZones" :key="zoneKey" :value="zone.id"
                        >{{ zone.displayName }}
                      </option>
                    </select>
                  </div>
                </fieldset>
              </div>
            </section-header>

            <hr class="rule" />
            <store-hours
              v-cloak
              :store-data="storeData"
              :day-labels="storeData.dayLabels"
              :add-range-text="bp('textAddRange')"
            />
          </div>
        </div>
      </div>

      <div class="buttons my-5 pb-5">
        <trek-button primary :disabled="isSubmitting" @click="validate">
          <span v-html="bp('labelSubmit')"></span>
        </trek-button>
        <trek-button secondary :disabled="isSubmitting" @click="cancelSubmit">
          <span v-html="bp('labelCancel')"></span>
        </trek-button>
        <trek-loading page :is-loading="isSubmitting" />
      </div>
    </form>
  </div>
</template>

<script>
import Vue from 'vue';
import {mask as maskDirective} from 'vue-the-mask';
import axios from 'axios';
import find from 'lodash/find';
import omit from 'lodash/omit';
import values from 'lodash/values';
import unescape from 'lodash/unescape';
import cloneDeep from 'lodash/cloneDeep';
import Heading from '@/components/Heading';
import FormGroup from '@/components/FormGroup';
import TrekButton from '@/components/TrekButton';
import Subheading from '@/components/Subheading';
import TrekLoading from '@/components/TrekLoading';
import SectionHeader from '@/components/SectionHeader';
import StoreLocationMap from '@/components/StoreLocationMap';
import StoreHours from '@/components/containers/my-company/StoreHours';

export default {
  components: {
    Heading,
    FormGroup,
    Subheading,
    SectionHeader,
    StoreLocationMap,
    StoreHours,
    TrekButton,
    TrekLoading,
  },

  directives: {
    mask(element, maskObj) {
      if (!maskObj.value.mask) return;

      maskDirective(element, maskObj);
    },
  },

  props: {
    baseProperties: {
      type: Array,
      default() {
        return [];
      },
    },
    initialStoreData: {
      type: Object,
      default: undefined,
    },
    distanceUnits: {
      type: Array,
      default: undefined,
    },
    postUrl: {
      type: String,
      default: undefined,
    },
    returnUrl: {
      type: String,
      default: undefined,
    },
    postCodeFormat: {
      default: '',
      type: String,
      required: true,
    },
    postCodeLength: {
      type: Number,
      required: true,
      default: undefined,
    },
  },

  data() {
    return {
      storeData: this.initialStoreData,
      contextPath: ACC.config.encodedContextPath || '',
      isSubmitting: false,
      formattedAddress: {},
      maxRadius: this.initialStoreData.countryIso === 'NL' ? 50 : 99,
      isPostCodeLength: this.postCodeLength > 0 ? this.postCodeLength : false,
      postCodeFormatOptions: {
        mask: this.postCodeFormat,
        tokens: {
          L: {
            pattern: /[a-zA-Z]/,
          },
          N: {
            pattern: /\d/,
          },
        },
      },
    };
  },

  computed: {
    timeZones() {
      const zones = find(
        this.storeData.options.timeZones,
        (timeZone) => timeZone[this.storeData.countryIso] !== undefined
      );
      return zones ? zones[this.storeData.countryIso] : [];
    },

    countries() {
      const countriesList = find(
        this.storeData.options.countries,
        (country) => country.isoCode === this.storeData.countryIso
      );
      return countriesList ? countriesList : [];
    },

    states() {
      const selectedState = find(
        this.storeData.options.countryRegionMap.US,
        (state) => values(state)[0].isocode === this.storeData.regionIso
      );
      return selectedState ? selectedState : [];
    },

    location() {
      return {
        latitude: parseFloat(this.storeData.latitude),
        longitude: parseFloat(this.storeData.longitude),
      };
    },

    distanceUnitError() {
      if (!this.$validator.errors.items || !this.$validator.errors.items.length) {
        return '';
      }
      const error = this.$validator.errors.items.find((error) => error.field === 'delivery-radius-unit');
      return !error || !('msg' in error) ? '' : error.msg;
    },
  },

  mounted() {
    this.initializeCustomErrorMessages();
    this.updateAddress();
  },

  methods: {
    values,

    postCodeToUppercase() {
      if (this.postCodeFormat) {
        this.storeData.postalCode = this.storeData.postalCode.toUpperCase();
      }
    },

    initializeCustomErrorMessages() {
      const dictionary = {
        custom: {
          'delivery-radius-unit': {
            required: this.bp('labelDeliveryRadiusUnitMessage'),
          },
          'text-address-postal-code': {
            length: this.bp('invalidPostalCode'),
          },
        },
      };
      this.$validator.localize(this.$store.state.vvLocale, dictionary);
    },

    updateAddress() {
      if (this.storeData.line1 && this.storeData.town && this.storeData.postalCode) {
        this.formattedAddress = {
          address: `${this.storeData.line1}, ${this.storeData.town},${this.storeData.regionIso},${this.storeData.postalCode}`,
        };
      }
    },

    unescapeString(string) {
      return unescape(string);
    },

    updateLatLng(event) {
      this.storeData.latitude = event.lat;
      this.storeData.longitude = event.lng;
    },

    updateCountryName() {
      this.storeData.countryName = this.countries.name;
      this.storeData.countryIso = this.countries.isoCode;
      if (this.timeZones && this.timeZones.length) {
        this.storeData.timeZone = this.timeZones[0].id;
      }
    },

    validate() {
      this.$validator.validate().then((valid) => {
        if (!valid) {
          const firstErrorFieldName = Object.keys(this.$validator.errors.collect())[0];
          const firstErrorFieldEl = document.querySelector(`[name="${firstErrorFieldName}"]`);
          this.$scrollTo(firstErrorFieldEl);
        } else {
          this.submitChanges();
        }
      });
    },

    getDataToSave() {
      return omit(cloneDeep(this.storeData), ['options']);
    },

    submitChanges() {
      const token = ACC.config.CSRFToken;
      Vue.set(this.storeData, 'csrfToken', token);

      this.$validator.validateAll().then((result) => {
        if (!result) return (this.saveInProgress = false);

        this.isSubmitting = true;
        axios
          .post(`${this.postUrl}?CSRFToken=${token}`, this.getDataToSave())
          .then((response) => {
            if (response.data.meta.feedback.type === 'alert') {
              this.$notify({
                type: response.data.meta.feedback.type,
                message: response.data.meta.feedback.message.basePropertyValue,
              });
              this.isSubmitting = false;
            } else {
              document.location.href = `${this.contextPath}${this.returnUrl}`;
            }
          })
          .catch((error) => {
            console.log(error);
            this.isSubmitting = false;
          });
      });
    },

    cancelSubmit() {
      document.location.href = `${this.contextPath}${this.returnUrl}`;
    },
  },
};
</script>
